import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { restoreFile, type RemovedFile, type UploadModule } from '@/api/files';

export type StorageMode = 'LOCAL' | 'S3';
export type StorageProvider = 'AWS' | 'MINIO' | 'WASABI' | 'R2' | 'DO_SPACES' | 'OTHER';
/** A settings block as the server returns it; forms address its keys by dotted path. */
export type ConfigBlock = Record<string, unknown>;

export interface StorageUsage {
  used_bytes: number;
  reserved_bytes: number;
  file_count: number;
}

export interface StorageProfile {
  id: string;
  name: string;
  provider: StorageProvider;
  endpoint_url: string | null;
  region: string | null;
  bucket: string;
  force_path_style: boolean;
  credentials_mode: 'KEYS' | 'INSTANCE';
  isolation: 'STS' | 'PREFIX';
  sts_role_arn: string | null;
  sts_duration_seconds: number | null;
  sse: 'AES256' | 'KMS' | 'NONE';
  kms_key_id: string | null;
  storage_class: string;
  checksum_supported: boolean;
  is_active: boolean;
  health_status: string | null;
  health_error: string | null;
  health_checked_at: string | null;
  cors_verified_at: string | null;
  updated_at: string | null;
  access_key_id_set: boolean;
  secret_access_key_set: boolean;
  sts_external_id_set: boolean;
}

/** Omitted fields keep their value; a secret sent as '' is cleared. */
export type ProfileIn = Partial<Pick<StorageProfile,
  'name' | 'provider' | 'endpoint_url' | 'region' | 'bucket' | 'force_path_style' | 'credentials_mode' | 'isolation'
  | 'sts_role_arn' | 'sts_duration_seconds' | 'sse' | 'kms_key_id' | 'storage_class' | 'checksum_supported' | 'is_active'>>
  & { access_key_id?: string; secret_access_key?: string; sts_external_id?: string };

export interface ProfileCheck {
  ok: boolean;
  steps: { step: string; ok: boolean; detail: string }[];
}

export interface StorageView {
  platform: ConfigBlock & {
    mode: StorageMode;
    active_profile_id: string | null;
    key_prefix: string | null;
    quota_ceiling_bytes: number | null;
  };
  tenant: ConfigBlock & { default_user_quota_bytes: number | null };
  mode: StorageMode;
  platform_editable: boolean;
  profiles_editable: boolean;
  profiles: StorageProfile[];
  /** What a new server of each provider starts with. */
  provider_defaults: Record<StorageProvider, ProfileIn>;
  usage: StorageUsage;
  queue: { pending_sync: number; failed: number };
  /** The last integrity run; null until the checks have run once. */
  integrity: IntegritySummary | null;
}

/** What the nightly integrity checks found and fixed on their last run. */
export interface IntegritySummary {
  at: string;
  /** Files whose record no longer holds them, now waiting out the orphan period. */
  orphaned: number;
  /** Orphaned files whose record lists them again, attached once more. */
  reattached: number;
  /** Records that listed an unlinked upload of their own saver, linked to it. */
  relinked: number;
  /** Records still listing a file that is missing, removed or linked elsewhere. */
  dangling: number;
  /** Live files whose bytes are not where they should be. */
  missing: number;
  /** Files whose bytes were checked this run. */
  verified: number;
  /** Bytes no record uses, removed. */
  strays: number;
  warnings: string[];
}

export interface RecycleBinFilters {
  module?: string;
  reason?: string;
}
export interface RecycleBinPage {
  items: RemovedFile[];
  total: number;
}

export interface ConfigPatch {
  platform?: ConfigBlock;
  tenant?: ConfigBlock;
}

export interface StoragePolicy {
  id: string;
  scope_type: 'ROLE' | 'USER';
  scope_value: string;
  quota_bytes: number | null;
  max_file_bytes: number | null;
  allowed_extensions: string[] | null;
  can_upload: boolean | null;
  note: string | null;
}
export type PolicyIn = Omit<StoragePolicy, 'id'>;

export type UsageGroup = 'user' | 'module' | 'role';
export interface UsageRow {
  user_id?: string;
  name?: string | null;
  email?: string | null;
  role?: string | null;
  module?: UploadModule;
  used_bytes: number;
  reserved_bytes?: number;
  file_count: number;
}

export interface StorageEvent {
  id: number;
  at: string;
  event: string;
  outcome: 'OK' | 'FAIL' | 'DENIED';
  file_id: string | null;
  user_id: string | null;
  module: string | null;
  driver: string | null;
  bytes: number | null;
  duration_ms: number | null;
  error_code: string | null;
  message: string | null;
  request_id: string | null;
  ip: string | null;
  meta: Record<string, unknown> | null;
}
export interface EventFilters {
  event?: string;
  outcome?: string;
  module?: string;
  before_id?: number;
}
export interface EventPage {
  items: StorageEvent[];
  next_before_id: number | null;
}

export interface FleetRow {
  tenant_key: string;
  mode: StorageMode;
  used_bytes: number;
  file_count: number;
  quota_ceiling_bytes: number | null;
  queue: { pending_sync: number; failed: number };
  profiles: { name: string; health: string | null }[];
}

/**
 * One workspace's storage controls. Settings → Storage and the Platform
 * Console reach the same service through different routes and credentials;
 * the screens use whichever methods the side they run on offers.
 */
export interface StorageAdmin {
  /** Keeps one workspace's cached data apart from another's. */
  scope: string;
  load(): Promise<StorageView>;
  save(patch: ConfigPatch): Promise<StorageView>;
  saveProfile(id: string | null, body: ProfileIn): Promise<StorageProfile>;
  deleteProfile(id: string): Promise<unknown>;
  testProfile(id: string): Promise<ProfileCheck>;
  // The workspace ADMIN's side only.
  listPolicies?(): Promise<StoragePolicy[]>;
  savePolicy?(id: string | null, body: PolicyIn): Promise<StoragePolicy>;
  deletePolicy?(id: string): Promise<unknown>;
  usage?(groupBy: UsageGroup): Promise<UsageRow[]>;
  events?(filters: EventFilters): Promise<EventPage>;
  retry?(fileId: string): Promise<unknown>;
  recycleBin?(filters: RecycleBinFilters & { offset: number; limit: number }): Promise<RecycleBinPage>;
  restoreFile?(fileId: string): Promise<unknown>;
  /** A short-lived link to download a removed file. */
  removedFileUrl?(fileId: string): Promise<{ url: string }>;
  runIntegrity?(): Promise<IntegritySummary>;
  // The platform operator's side only.
  applyCors?(profileId: string, origins: string[]): Promise<StorageProfile>;
  applyLifecycle?(profileId: string, days: number): Promise<{ prefix: string; days: number }>;
  relocate?(profileId: string): Promise<{ relocate_to: string }>;
}

const SETTINGS = '/settings/storage';

export const tenantStorageAdmin: StorageAdmin = {
  scope: 'tenant',
  load: () => api.get<StorageView>(SETTINGS),
  save: (patch) => api.put<StorageView>(SETTINGS, patch),
  saveProfile: (id, body) => (id
    ? api.put<StorageProfile>(`${SETTINGS}/profiles/${id}`, body)
    : api.post<StorageProfile>(`${SETTINGS}/profiles`, body)),
  deleteProfile: (id) => api.delete(`${SETTINGS}/profiles/${id}`),
  testProfile: (id) => api.post<ProfileCheck>(`${SETTINGS}/profiles/${id}/test`),
  listPolicies: () => api.get<StoragePolicy[]>(`${SETTINGS}/policies`),
  savePolicy: (id, body) => (id
    ? api.put<StoragePolicy>(`${SETTINGS}/policies/${id}`, body)
    : api.post<StoragePolicy>(`${SETTINGS}/policies`, body)),
  deletePolicy: (id) => api.delete(`${SETTINGS}/policies/${id}`),
  usage: (groupBy) => api.get<UsageRow[]>(`${SETTINGS}/usage`, { group_by: groupBy }),
  events: (filters) => api.get<EventPage>(`${SETTINGS}/events`, { ...filters }),
  retry: (fileId) => api.post(`${SETTINGS}/files/${fileId}/retry`),
  recycleBin: (filters) => api.get<RecycleBinPage>(`${SETTINGS}/recycle-bin`, { ...filters }),
  restoreFile,
  removedFileUrl: (fileId) => api.get<{ url: string }>(`${SETTINGS}/recycle-bin/${fileId}/url`),
  runIntegrity: () => api.post<IntegritySummary>(`${SETTINGS}/integrity/run`),
};

/** The console's authenticated JSON fetch (it carries the operator's token, not a workspace session). */
export type JsonCall = (path: string, init?: RequestInit) => Promise<unknown>;

export function platformStorageAdmin(call: JsonCall, tenantKey: string): StorageAdmin {
  const base = `/tenants/${encodeURIComponent(tenantKey)}/storage`;
  const send = <T>(path: string, method: string, body?: unknown) =>
    call(path, { method, body: body === undefined ? undefined : JSON.stringify(body) }) as Promise<T>;
  return {
    scope: `platform:${tenantKey}`,
    load: () => send<StorageView>(base, 'GET'),
    save: (patch) => send<StorageView>(base, 'PUT', patch),
    saveProfile: (id, body) => (id
      ? send<StorageProfile>(`${base}/profiles/${id}`, 'PUT', body)
      : send<StorageProfile>(`${base}/profiles`, 'POST', body)),
    deleteProfile: (id) => send(`${base}/profiles/${id}`, 'DELETE'),
    testProfile: (id) => send<ProfileCheck>(`${base}/profiles/${id}/test`, 'POST'),
    applyCors: (id, origins) => send<StorageProfile>(`${base}/profiles/${id}/apply-cors`, 'POST', { origins }),
    applyLifecycle: (id, days) => send(`${base}/profiles/${id}/apply-lifecycle`, 'POST', { days }),
    relocate: (id) => send(`${base}/relocate`, 'POST', { profile_id: id }),
  };
}

export function fleetStorageUsage(call: JsonCall): Promise<FleetRow[]> {
  return call('/storage/usage') as Promise<FleetRow[]>;
}

export const storageKey = (admin: StorageAdmin, ...rest: unknown[]) => ['storage-admin', admin.scope, ...rest];

export function useStorageView(admin: StorageAdmin, enabled = true) {
  return useQuery({ queryKey: storageKey(admin), queryFn: () => admin.load(), enabled });
}

/** Any change to a workspace's storage: refreshes its screens and the upload limits every uploader reads. */
export function useStorageAction<A, R>(admin: StorageAdmin, action: (args: A) => Promise<R>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: action,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['storage-admin', admin.scope] });
      if (admin.scope === tenantStorageAdmin.scope) qc.invalidateQueries({ queryKey: ['files', 'limits'] });
    },
  });
}
